import { isNil } from "lodash";

import Sentry from "@similarweb/sentry-logger";

import type {
    IStepState,
    WizardRole,
} from "app/pages/digital-suite/home-page/components/ActivationWizard/ActivationWizardUtils";
import { EStepState } from "app/pages/digital-suite/home-page/components/ActivationWizard/ActivationWizardUtils";
import { DefaultFetchService } from "app/services/fetchService";

interface IStepsData {
    index: number;
    state: EStepState;
}

const API_URL = "/api/userdata/state-properties";
const fetchService = DefaultFetchService.getInstance();
const cachedStates = {};

export enum EnumWizardItems {
    ActivationWizardItems = "ActivationWizardItems",
    IsActivationWizardOpen = "isActivationWizardOpen",
    ShowSideBarGetStarted = "showSideBarGetStarted",
    IsAccordionOpen = "isAccordionOpen",
    IsCreateDashboardFromHP = "isCreateDashboardFromHP",
    IsCreateTrackFromHP = "isCreateTrackFromHP",
    SeenProjectSetupWizard = "SeenProjectSetupWizard",
}

const getWizardKeyPrefix = (role: WizardRole) => `wizard-${role}`;

const composeStepStateKey = (role: WizardRole, stepIndex: number) =>
    `${getWizardKeyPrefix(role)}.stepState.${stepIndex}`;

const composeCustomDataKey = (role: WizardRole, dataKey: string) => `${getWizardKeyPrefix(role)}.customData.${dataKey}`;

export const getActivationWizardItems = (item: string): boolean => {
    const activationWizardItems = JSON.parse(localStorage.getItem(EnumWizardItems.ActivationWizardItems)) ?? {};
    if (!isNil(activationWizardItems?.[item])) {
        return activationWizardItems[item];
    }
    return null;
};

export const getWithInitActivationWizardItems = (item: string): boolean => {
    const activationWizardItemsLS = JSON.parse(localStorage.getItem(EnumWizardItems.ActivationWizardItems)) ?? {};
    if (!isNil(activationWizardItemsLS?.[item])) {
        return activationWizardItemsLS[item];
    } else {
        localStorage.setItem(EnumWizardItems.ActivationWizardItems, JSON.stringify(activationWizardItemsLS));
        return true;
    }
};

export const setActivationWizardItems = (item: string, isOpen: boolean) => {
    const activationWizardItems = JSON.parse(localStorage.getItem(EnumWizardItems.ActivationWizardItems)) ?? {};
    activationWizardItems[item] = isOpen;
    localStorage.setItem(EnumWizardItems.ActivationWizardItems, JSON.stringify(activationWizardItems));
};

const fetchStepsState = (role: WizardRole): Promise<Array<IStepState>> => {
    //create a regex that matches a step's state key
    const genericStateKey = composeStepStateKey(role, -1);
    const regexStr = genericStateKey.replace(/\./g, "\\.").replace("-1", "[0-9]+");
    const stateKeyRegex = new RegExp(`^${regexStr}$`);

    return fetchService
        .get(`${API_URL}/${getWizardKeyPrefix(role)}`)
        .then((stepsData: IStepsData) => {
            if (stepsData) {
                return (
                    Object.entries(stepsData)
                        //filter out the ones that refer to the step's completion state
                        .filter(([key, _]) => stateKeyRegex.test(key))
                        //convert into a state object
                        .map(([key, val]) => ({
                            index: parseInt(key.split(".")[2]),
                            state: val,
                        }))
                );
            } else {
                return [];
            }
        })
        .catch((error) => {
            Sentry.errorTracker({
                message: "Error fetching steps state",
                e: error,
                level: "error",
            });
            return null;
        });
};

export const getWizardCustomData = async (role: WizardRole, dataKey: string): Promise<unknown> => {
    const key = composeCustomDataKey(role, dataKey);
    return await fetchService
        .get(`${API_URL}/${key}`)
        .then((res) => res[key])
        .catch((error) => {
            Sentry.errorTracker({
                message: "Error fetching wizard custom data",
                e: error,
                level: "error",
            });
            return undefined;
        });
};

export const setWizardCustomData = (role: WizardRole, dataKey: string, value: unknown): void => {
    const key = composeCustomDataKey(role, dataKey);
    fetchService.post(API_URL, { [key]: value }).catch((error) => {
        Sentry.errorTracker({
            message: "Error setting wizard custom data",
            e: error,
            level: "error",
        });
    });
};

export const getStepsState = async (role: WizardRole): Promise<Array<IStepState>> =>
    (cachedStates[role] ??= await fetchStepsState(role));

export const setStepsState = (role: WizardRole, stepsState: Array<IStepState>): Promise<unknown> => {
    const payload = stepsState.reduce((acc, curr) => {
        acc[composeStepStateKey(role, curr.index)] = curr.state;
        return acc;
    }, {});

    return fetchService
        .post(API_URL, payload)
        .catch((error) => {
            Sentry.errorTracker({
                message: "Error setting steps state",
                e: error,
                level: "error",
            });
        })
        .finally(() => (cachedStates[role] = null));
};

export const getShowGetStarted = (): boolean =>
    getActivationWizardItems(EnumWizardItems.ShowSideBarGetStarted) ?? false;

export const setShowGetStarted = (value: boolean): void => {
    if (value) {
        setActivationWizardItems(EnumWizardItems.ShowSideBarGetStarted, true);
    } else {
        setActivationWizardItems(EnumWizardItems.ShowSideBarGetStarted, false);
        setActivationWizardItems(EnumWizardItems.IsActivationWizardOpen, true);
    }
};

export const resetSteps = (role: WizardRole, stepsState: Array<IStepState>): Promise<unknown> => {
    const payload = stepsState.reduce((acc, curr) => {
        acc[composeStepStateKey(role, curr.index)] = EStepState.INCOMPLETE;
        return acc;
    }, {});
    return fetchService
        .post(API_URL, payload)
        .catch((error) => {
            Sentry.errorTracker({
                message: "Error resetting steps state",
                e: error,
                level: "error",
            });
        })
        .finally(() => (cachedStates[role] = null));
};
